import { FC, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";
import { SxProps, Theme } from "@mui/material/styles";
import {
  DataGrid,
  GridColDef,
  gridClasses,
  GridPaginationModel,
  GridRow,
  GridRowParams,
  GridRowProps,
} from "@mui/x-data-grid";

import { ExportDetails } from "../../../types/niffler";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import { formatNumber, abbrevNumberFormat } from "../../../utils/numberUtils";
import { StatusBadge } from "./components/StatusBadge";
import { ExportActions } from "./components/ExportActions";
import { formatDate, getExportTypeDisplay } from "./utils/helpers";
import Stack from "@mui/material/Stack";
import { LabelDisplayedRowsArgs } from "@mui/material/TablePagination";
import { EXPORT_CONSTANTS, EXPORT_STATUS } from "./constants";

// DataGrid styling matching CompanyList patterns
const dataGridStyles: SxProps<Theme> = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },
  [`& .${gridClasses.columnSeparator}`]: {
    [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
      display: "none",
    },
  },
  "& .MuiDataGrid-columnHeader": {
    borderBottom: "none !important",
  },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
    "& .MuiIconButton-root": {
      padding: 0,
    },
    "& .MuiTouchRipple-root": {
      display: "none",
    },
  },
  "&, [class^=MuiDataGrid]": { border: "none" },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: "none",
  },
  "& .MuiDataGrid-filler": {
    display: "none",
  },
  ".MuiDataGrid-scrollbarFiller": {
    display: "none",
  },
  "& .MuiDataGrid-scrollbar--vertical": {
    ...lightScrollbarStyle,
  },
  "& .MuiDataGrid-scrollbar--horizontal": {
    ...lightScrollbarStyle,
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    color: "#3a3a3a",
    fontSize: "12px",
    fontWeight: "bold",
  },
};

// Row slot that adds a stable data-testid per export row for e2e targeting
const ExportRow = (props: GridRowProps) => (
  <GridRow {...props} data-testid={`settings-export-row-${props.rowId}`} />
);

// Skeleton row type for loading state
interface SkeletonRow {
  id: string;
  isSkeleton: true;
}

// Generate skeleton rows for loading state
const generateSkeletonRows = (count: number): SkeletonRow[] => {
  return Array.from({ length: count }, (_, index) => ({
    id: `skeleton-${index}`,
    isSkeleton: true,
  }));
};

// Status Filter Component
interface StatusFilterProps {
  statusFilter: EXPORT_STATUS | undefined;
  onStatusFilterChange: (status: EXPORT_STATUS | undefined) => void;
}

const StatusFilter: FC<StatusFilterProps> = ({
  statusFilter,
  onStatusFilterChange,
}) => {
  const handleStatusFilterChange = useCallback(
    (value: string) => {
      onStatusFilterChange(value as EXPORT_STATUS);
    },
    [onStatusFilterChange]
  );

  const handleClearFilter = useCallback(() => {
    onStatusFilterChange(undefined);
  }, [onStatusFilterChange]);

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <FormControl sx={{ width: 200 }}>
        <InputLabel variant="standard">Status</InputLabel>
        <Select
          data-testid="settings-exports-status-select"
          value={statusFilter || ""}
          label="Status"
          variant="standard"
          displayEmpty
          onChange={(e) => handleStatusFilterChange(e.target.value)}
          endAdornment={
            statusFilter && (
              <IconButton
                data-testid="settings-exports-status-clear-btn"
                size="small"
                onClick={handleClearFilter}
                sx={{ mr: 2 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }
        >
          <MenuItem value={EXPORT_STATUS.IN_PROGRESS}>Processing</MenuItem>
          <MenuItem value={EXPORT_STATUS.COMPLETED}>Completed</MenuItem>
          <MenuItem value={EXPORT_STATUS.FAILED}>Failed</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
};

interface ExportTableProps {
  historyData: {
    data: ExportDetails[];
    loading: boolean;
    totalCount: number;
    paginationModel: GridPaginationModel;
    setPaginationModel: (model: GridPaginationModel) => void;
    refetch: () => Promise<void>;
  };
  onViewDetails: (exportDetails: ExportDetails) => void;
  statusFilter: EXPORT_STATUS | undefined;
  onStatusFilterChange: (status: EXPORT_STATUS | undefined) => void;
}

export const ExportTable: FC<ExportTableProps> = ({
  historyData,
  onViewDetails,
  statusFilter,
  onStatusFilterChange,
}) => {
  const {
    data,
    loading,
    totalCount,
    paginationModel,
    setPaginationModel,
    refetch,
  } = historyData;

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      setPaginationModel(newModel);
    },
    [setPaginationModel]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      if (!params.row.isSkeleton) {
        onViewDetails(params.row);
      }
    },
    [onViewDetails]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="70%" />;
          }
          return <Typography variant="body2">{params.row.id}</Typography>;
        },
      },
      {
        field: "exportType",
        headerName: "Export Type",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="80%" />;
          }
          return (
            <Typography variant="body2" noWrap>
              {getExportTypeDisplay(params.row)}
            </Typography>
          );
        },
      },
      {
        field: "format",
        headerName: "Format",
        width: 80,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="60%" />;
          }
          return <Typography variant="body2">{params.row.format}</Typography>;
        },
      },
      {
        field: "records",
        headerName: "Records",
        width: 120,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="rounded" width={80} height={24} />;
          }
          const count = params.row.rowCount;
          const displayValue = count
            ? abbrevNumberFormat(count).toUpperCase()
            : "-";
          const fullValue = count ? formatNumber(count) : "-";

          return (
            <Tooltip title={fullValue} arrow>
              <Chip
                label={displayValue}
                size="small"
                variant="filled"
                sx={{ minWidth: 60, bgcolor: "grey.200" }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: "requested",
        headerName: "Requested",
        width: 200,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="90%" />;
          }
          return (
            <Typography variant="body2">
              {formatDate(params.row.dateTime)}
            </Typography>
          );
        },
      },
      {
        field: "completed",
        headerName: "Completed",
        width: 200,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="90%" />;
          }
          return (
            <Typography variant="body2">
              {params.row.completedDateTime
                ? formatDate(params.row.completedDateTime)
                : "-"}
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="rounded" width={80} height={24} />;
          }
          return <StatusBadge status={params.row.status} />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="rounded" width={80} height={32} />;
          }
          return (
            <ExportActions
              exportDetails={params.row}
              onViewDetails={onViewDetails}
              onActionComplete={refetch}
            />
          );
        },
      },
    ],
    [onViewDetails, refetch]
  );

  const skeletonRows = useMemo(
    () => generateSkeletonRows(paginationModel.pageSize),
    [paginationModel.pageSize]
  );

  const rows = useMemo(() => {
    if (loading) return skeletonRows;

    return data.map((exportItem) => ({ ...exportItem, isSkeleton: false }));
  }, [loading, data, skeletonRows]);

  return (
    <Stack direction="column" height="100%">
      {/* Status Filter */}
      <StatusFilter
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
      />

      {/* Results Count */}
      {loading ? (
        <Stack direction="row" alignItems="center" height={24}>
          <Skeleton
            variant="text"
            width={24}
            height={40}
            sx={{ display: "inline-block", mr: 1 }}
          />
          <Typography variant="h5">exports found</Typography>
        </Stack>
      ) : (
        <Typography variant="h5" height={24}>
          {`${formatNumber(totalCount || 0)} exports found`}
        </Typography>
      )}

      {/* Data Grid */}
      <Box sx={{ width: "100%", flexGrow: 1, marginLeft: -1 }}>
        <DataGrid
          data-testid="settings-exports-grid"
          rows={rows}
          columns={columns}
          slots={{ row: ExportRow }}
          pageSizeOptions={EXPORT_CONSTANTS.PAGINATION.PAGE_SIZE_OPTIONS}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          disableColumnFilter
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnResize
          disableColumnSelector
          disableColumnSorting
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              labelDisplayedRows: ({
                from,
                to,
                count,
              }: LabelDisplayedRowsArgs) => {
                return `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`;
              },
            },
          }}
          sx={dataGridStyles}
        />
      </Box>
    </Stack>
  );
};
